import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ClipboardList, WifiOff } from "lucide-react";
import { InfoTip } from "./InfoTip";

// Collapsible section state, persisted per browser.
export function useCollapse(id, defaultOpen = true) {
  const key = `kairos_rta_sec_${id}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(key); return v === null ? defaultOpen : v !== "0"; } catch { return defaultOpen; }
  });
  const toggle = useCallback(() => setOpen((o) => {
    try { localStorage.setItem(key, o ? "0" : "1"); } catch {}
    return !o;
  }), [key]);
  // "Focus mode": collapse/expand every section at once (except `detail.except`).
  useEffect(() => {
    const h = (e) => {
      const { except, open: wantOpen } = e.detail || {};
      const next = wantOpen || id === except;
      try { localStorage.setItem(key, next ? "1" : "0"); } catch {}
      setOpen(next);
    };
    window.addEventListener("kairos-collapse-all", h);
    return () => window.removeEventListener("kairos-collapse-all", h);
  }, [key, id]);
  return [open, toggle];
}

export const SecToggle = ({ open, onToggle, testid }) => (
  <button type="button" data-testid={testid} onClick={onToggle} title={open ? "Collapse section" : "Expand section"}
          className="ml-auto shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
    {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
  </button>
);

// Render a person's name with their CISCO ID in brackets when present.
export const nid = (name, cisco) => (cisco ? `${name} (${cisco})` : name);

export function fmt(sec) {
  sec = Math.max(0, Math.floor(sec));
  const m = Math.floor(sec / 60), r = sec % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
export function ago(iso, nowMs) {
  if (!iso) return "";
  const s = Math.max(0, (nowMs - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}
export function secsUntil(iso, nowMs) { return iso ? (new Date(iso).getTime() - nowMs) / 1000 : 0; }
export { fmtHM as hm } from "./timeFmt";

const STATUS = {
  REQUESTED: "bg-amber-100 text-amber-700", QUEUED: "bg-sky-100 text-sky-700",
  APPROVED: "bg-indigo-100 text-indigo-700", ACTIVE: "bg-emerald-100 text-emerald-700",
  COMPLETED: "bg-gray-100 text-gray-600", DENIED: "bg-rose-100 text-rose-700",
  EXPIRED: "bg-gray-100 text-gray-500", CANCELLED: "bg-gray-100 text-gray-400",
};
export const Pill = ({ s }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>
);

export function PlannedChip({ r }) {
  const p = r.planned;
  if (r.adherence === "on_plan")
    return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5"><ClipboardList size={11} /> On plan · {p.break_type} {p.start}–{p.end}</span>;
  if (r.adherence === "off_plan")
    return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5"><ClipboardList size={11} /> Planned: {p.break_type} {p.start}–{p.end} (off-plan)</span>;
  return <span className="text-[11px] font-medium text-gray-400 bg-gray-50 rounded px-1.5 py-0.5">no planned break now</span>;
}

// polling hook — third element flags a stale read path (last fetch failed)
export function usePoll(fn, ms, deps = []) {
  const [data, setData] = useState(null);
  const [stale, setStale] = useState(false);
  const saved = useRef(fn);
  saved.current = fn;
  const ctrl = useRef(null);       // AbortController of the in-flight request
  const inflight = useRef(false);
  const run = useCallback((force) => {
    // Interval ticks skip while a request is still running (no stacking / connection
    // starvation); a forced (manual) reload preempts the in-flight one for instant feedback.
    if (inflight.current && !force) return;
    if (ctrl.current) ctrl.current.abort();
    const ac = new AbortController();
    ctrl.current = ac;
    inflight.current = true;
    Promise.resolve(saved.current(ac.signal))
      .then((d) => { if (!ac.signal.aborted) { setData(d); setStale(false); } })
      .catch(() => { if (!ac.signal.aborted) setStale(true); })
      .finally(() => { if (ctrl.current === ac) inflight.current = false; });
  }, []);
  const reload = useCallback(() => run(true), [run]);
  useEffect(() => {
    run(false);
    if (!ms) return;
    const t = setInterval(() => run(false), ms);
    return () => { clearInterval(t); if (ctrl.current) ctrl.current.abort(); inflight.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [data, reload, stale];
}

export const StaleChip = ({ show }) => show ? (
  <span data-testid="stale-data-chip" title="The last data refresh failed — Kairos keeps retrying automatically. Numbers on screen may be out of date."
        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
    <WifiOff size={11} /> Live data stale — retrying
  </span>
) : null;

export const SectionH = ({ children, info, details, infoTitle }) => (
  <h3 className="k-section mb-3 flex items-center gap-2">
    <span className="h-3 w-1 rounded-full brand-grad" />
    {children}
    {info && <InfoTip testid={`info-${String(infoTitle || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} title={infoTitle} text={info} details={details} side="right" />}
  </h3>
);
